import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';

import { MessageItem } from '@apps/web/components/shared/messages/message_item';
import { MessagePending } from '@apps/web/components/shared/messages/message_pending';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { type StagedMessage } from '@libraries/typescript/api_client';

type PendingMessageCardProps = {
    readonly pendingMessage: StagedMessage;
    readonly organizationId: string;
    readonly timezone: string;
    readonly title: string;
    readonly titleTo?: string;
    readonly titleSearch?: Record<string, string>;
    readonly subtitle?: string;
    readonly onUpdate: () => void;
};

export function PendingMessageCard({
    pendingMessage,
    organizationId,
    timezone,
    title,
    titleTo,
    titleSearch,
    subtitle,
    onUpdate,
}: PendingMessageCardProps) {
    const caregiverMessageCount = pendingMessage.caregiver_message_count ?? 0;
    const additionalMessagesCount = Math.max(caregiverMessageCount - 1, 0);

    const caregiverMessagePreview = pendingMessage.last_caregiver_message ? (
        <div className="space-y-2">
            {additionalMessagesCount > 0 && (
                <div className="text-muted-foreground flex items-center gap-1 text-xs font-medium group-hover:text-blue-600">
                    +{additionalMessagesCount}{' '}
                    {additionalMessagesCount === 1 ? 'message' : 'messages'}
                    <ArrowUpRight className="h-3 w-3" />
                </div>
            )}
            <MessageItem
                message={pendingMessage.last_caregiver_message}
                timezone={timezone}
                showRating={false}
                showDate={true}
            />
        </div>
    ) : null;

    const caregiverMessageContent = titleTo ? (
        <Link
            to={titleTo}
            search={titleSearch}
            className="group hover:bg-muted/60 -m-1 block rounded-lg p-1 transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
            aria-label="Open caregiver conversation"
        >
            {caregiverMessagePreview}
        </Link>
    ) : (
        <div className="group">{caregiverMessagePreview}</div>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">
                    {titleTo ? (
                        <Link
                            to={titleTo}
                            search={titleSearch}
                            className="hover:text-blue-600"
                        >
                            {title}
                        </Link>
                    ) : (
                        title
                    )}
                </CardTitle>
                {subtitle && (
                    <p className="text-muted-foreground text-sm">{subtitle}</p>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {caregiverMessagePreview && caregiverMessageContent}
                <MessagePending
                    timezone={timezone}
                    stagedMessage={pendingMessage}
                    organizationId={organizationId}
                    onUpdate={onUpdate}
                />
            </CardContent>
        </Card>
    );
}
